import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import AboutHeroScene from "@/components/3d/AboutHeroScene";
import PackageShowcaseArt from "@/components/showcase/PackageShowcaseArt";
import Reveal from "@/components/ui/Reveal";
import { BOARDS, BRAND } from "@/constants/assets";
import { WORKFLOW, PRINCIPLES, wa } from "@/constants/content";
import usePageMeta from "@/hooks/usePageMeta";

const lanes = [
  {
    name: "Posters & social media creatives",
    role: "Daily visibility lane",
    img: BOARDS.postersSocial,
    variant: "posters",
  },
  {
    name: "Reels, branding & identity",
    role: "Motion + identity lane",
    img: BOARDS.reelsBranding,
    variant: "identity",
  },
  {
    name: "Industry packs",
    role: "7 vertical creative systems",
    img: BOARDS.restaurantSalon,
    variant: "industry",
  },
];

export default function About() {
  usePageMeta({
    title: "About · ThemeStudios Digital Designers",
    description:
      "ThemeStudios Digital Designers - remote design studio based in Madurai, serving small businesses across Tamil Nadu and beyond. Original layouts, copyright-safe briefs, WhatsApp-first workflow.",
    path: "/about",
  });

  return (
    <div data-testid="page-about" className="pt-28 md:pt-36">
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/5 min-h-[72svh] bg-[#050507]">
          <AboutHeroScene />

          <div className="relative z-10 px-6 py-8 md:px-12 md:py-12 lg:px-14 lg:py-14">
            <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-black/35 px-4 py-2 backdrop-blur-md">
              <img
                src={BRAND.logoLight}
                alt="ThemeStudios"
                className="h-8 w-auto"
                loading="eager"
              />
              <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-300">
                Logo atmosphere system
              </span>
            </div>

            <p className="mt-8 text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-5">
              * About the studio
            </p>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-7">
                <h1 className="h1-display text-white text-[8vw] md:text-[4.5vw] max-w-[18ch]">
                  ThemeStudios <span className="text-[#e03c31]">Digital Designers.</span>
                </h1>
                <p className="mt-7 max-w-3xl text-base md:text-lg text-zinc-200 leading-relaxed font-medium">
                  ThemeStudios is a <span className="text-white">remote design studio based in Madurai</span>,
                  serving small businesses across Tamil Nadu and beyond. We build posters, reels,
                  branding kits, ad creatives, business-profile assets and{" "}
                  <span className="text-white">industry-specific monthly creative systems</span> for
                  restaurants, salons, clinics, retail, startups, education and real-estate brands.
                </p>
                <p className="mt-5 max-w-2xl text-sm md:text-[15px] text-zinc-300/90 leading-relaxed">
                  Our 2026 catalog is structured around how Indian small businesses actually market
                  today - WhatsApp-first ordering, mobile-first creatives, and a clear pricing ladder
                  from Rs. 349 story support to Rs. 29,999 launch identity systems. No template
                  galleries, no copyright shortcuts.
                </p>
              </div>

              <div className="md:col-span-4 md:col-start-9">
                <div className="glass rounded-3xl p-7 md:p-8 bg-black/28">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold mb-3">
                    Merchant identity
                  </p>
                  <p className="font-cabinet font-bold text-base text-white mb-1">
                    ThemeStudios Digital Designers
                  </p>
                  <p className="text-zinc-400 text-xs">Remote design studio · Madurai, Tamil Nadu</p>
                  <ul className="mt-5 space-y-2.5 text-[13px] text-zinc-200">
                    <li>
                      <a
                        href={wa("package")}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-[#e03c31] transition-colors"
                      >
                        Official WhatsApp: +91 8903205676
                      </a>
                    </li>
                    <li>
                      <a
                        href={`mailto:${BRAND.email}`}
                        className="hover:text-[#e03c31] transition-colors"
                      >
                        Support: support@themestudios.in
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-24 md:mt-32">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
          * How we work
        </p>
        <h2 className="h2-section text-2xl md:text-4xl max-w-[24ch]">
          Original layouts.{" "}
          <span className="outline-text">Client-provided assets only. Copyright-safe briefs.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {PRINCIPLES.map((principle) => (
            <div
              key={principle.t}
              className="p-7 bg-[#0a0a0c] rounded-2xl border border-white/5 card-lift card-glow"
            >
              <h3 className="font-cabinet text-lg font-bold mb-2.5">{principle.t}</h3>
              <p className="text-zinc-400 text-[13px] leading-relaxed">{principle.b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-24 md:mt-32">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
          * The workflow
        </p>
        <h2 className="h2-section text-2xl md:text-4xl mb-3">
          Send the brief → Match the lane → Build → Revise & deliver
        </h2>
        <p className="text-zinc-400 max-w-2xl mb-12 text-sm md:text-[15px]">
          Fast briefing, focused revisions, clean delivery - the same flow whether you order a
          Rs. 349 story or a Rs. 29,999 launch pack.
        </p>

        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />
          <div className="space-y-10 md:space-y-20">
            {WORKFLOW.map((step, index) => (
              <Reveal key={step.n} delay={index * 80}>
                <div
                  className={`relative grid md:grid-cols-2 gap-6 md:gap-12 ${
                    index % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
                  }`}
                >
                  <div className="pl-12 md:pl-0 md:pr-12 md:text-right">
                    <span className="font-cabinet text-[#e03c31] text-xs tracking-widest">
                      Step {step.n}
                    </span>
                    <h3 className="font-cabinet text-xl md:text-3xl font-bold mt-1.5 leading-[1.05]">
                      {step.title}
                    </h3>
                  </div>
                  <div className="pl-12 md:pl-12">
                    <p className="text-zinc-400 text-sm md:text-[15px] leading-relaxed max-w-md">
                      {step.body}
                    </p>
                  </div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 top-1 h-3 w-3 rounded-full bg-[#e03c31] ring-4 ring-[#050505]" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-24 md:mt-32">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
              * The lanes
            </p>
            <h2 className="h2-section text-2xl md:text-4xl">What we ship daily.</h2>
          </div>
          <Link to="/portfolio" className="btn-magnetic btn-ghost">
            See all work <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {lanes.map((lane, index) => (
            <Reveal key={lane.name} delay={index * 80}>
              <div className="relative overflow-hidden rounded-2xl aspect-[4/5] group bg-[#0a0a0c] border border-white/5 card-lift">
                <PackageShowcaseArt
                  image={lane.img}
                  variant={lane.variant}
                  layout="portrait"
                  className="absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#e03c31] font-bold">
                    {lane.role}
                  </p>
                  <h3 className="font-cabinet text-lg md:text-xl font-bold mt-1 leading-snug">
                    {lane.name}
                  </h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-24 md:mt-32">
        <div className="rounded-[2rem] glass p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h2 className="h2-section text-2xl md:text-4xl max-w-xl">
            Ready to brief the studio? Two routes, same queue.
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={wa("brief")}
              target="_blank"
              rel="noreferrer"
              className="btn-magnetic"
              data-testid="about-whatsapp-cta"
            >
              <MessageCircle size={14} /> Start on WhatsApp
            </a>
            <Link to="/contact" className="btn-magnetic btn-ghost">
              Open Contact Us <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
